import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Activity, HeartPulse, ClipboardCheck } from 'lucide-react';

const ChronicConditions = () => {
  const conditions = [
    { title: "Hjerne", detail: "fx migræne, kronisk hovedpine" },
    { title: "Hjerte", detail: "fx forhøjet blodtryk, hjerteinsufficiens, forhøjet kolesterol" },
    { title: "Lunger", detail: "fx astma, KOL, høfeber" },
    { title: "Mavetarmsystemet", detail: "fx inflammatorisk tarmlidelse" },
    { title: "Urinvejssystemet", detail: "fx prostataforstørrelse, vandladningsgener hos mænd" },
    { title: "Gynækologiske system", detail: "fx kontrol af p-piller" },
    { title: "Hormonsygdomme", detail: "fx diabetes og stofskiftelidelse" },
    { title: "Hud", detail: "fx psoriasis" },
    { title: "Gigtlidelser og knoglelidelser", detail: "fx polymyalgia reumatika og osteoporose" },
    { title: "Psykiske / psykiatriske lidelser", detail: "fx kronisk depression, ADHD, angstlidelse" },
    { title: "Alkoholoverbrug eller kronisk forbrug af vanedannende medicin", detail: "" }
  ];

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>Kroniske Lidelser & Årskontrol | Hellerup Lægehus</title>
        <meta name="description" content="Få professionel opfølgning og årskontrol af kroniske lidelser (diabetes, KOL, hjerte, blodtryk m.m.) hos Hellerup Lægehus. Bestil tid online." />
        <meta property="og:title" content="Kroniske Lidelser & Årskontrol | Hellerup Lægehus" />
        <meta property="og:description" content="Få professionel opfølgning og årskontrol af kroniske lidelser (diabetes, KOL, hjerte, blodtryk m.m.) hos Hellerup Lægehus. Bestil tid online." />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-50 pt-20 pb-12 md:pt-24 md:pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-sans font-medium text-black mb-6 tracking-tight">
            Kroniske lidelser og årskontrol
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl leading-relaxed">
            Vi hjælper dig med at sikre den rette behandling og opfølgning på dine kroniske tilstande gennem regelmæssige kontroller.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Text Content */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <h2 className="text-2xl font-sans font-medium text-black mb-4 tracking-tight">
                  Planlægning af årskontrol
                </h2>
                <p className="text-slate-700 leading-relaxed text-lg">
                  Har du en kendt kronisk lidelse eller tilstand, som ikke følges på et hospitalsambulatorium eller hos en anden speciallæge, eller får du fast medicin, fx p-piller, bør du tale med os om planlægning af årskontrol.
                </p>
                <p className="text-slate-700 leading-relaxed">
                  Til en årskontrol vil vi oftest først tage blodprøver og evt. hjertediagram og herefter aftales tid hvor vi sammen med dig gennemgå din medicin, så vi sikrer os, at du vedvarende får den rette behandling.
                </p>
                
                <div className="my-12 py-8 border-y border-slate-100">
                  <p className="text-slate-900 leading-relaxed font-medium italic">
                    Man kan godt have flere kroniske lidelser eller tilstande, der kræver behandling eller kontroller. I disse tilfælde kan vi vurdere, at det er en fordel at se dig af flere omgange for at sikre, at den enkelte tilstand får rette opmærksomhed og behandling.
                  </p>
                </div>

                <p className="text-slate-700 leading-relaxed italic">
                  Nogle lidelser kræver i øvrigt, at man ses hyppigere end en gang om året. Andre, at man ikke behøver at blive set hvert år.
                </p>
              </div>

              <div className="pt-8">
                <h2 className="text-2xl font-sans font-medium text-black mb-6 tracking-tight">
                  Eksempler på kroniske lidelser
                </h2>
                <p className="text-slate-600 mb-8">
                  Herunder ses eksempler på tilstande, som vi typisk følger. Kontakt os til en individuel vurdering, hvis du får fast medicin eller har en lidelse, du ikke kontrolleres for.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {conditions.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 border border-slate-100">
                      <Check className="w-5 h-5 text-black mt-0.5 flex-shrink-0" />
                      <div>
                        <span className="font-medium text-slate-900 block">{item.title}</span>
                        {item.detail && (
                          <span className="text-sm text-slate-500">{item.detail}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar Info */}
            <div className="lg:col-span-5">
              <div className="sticky top-32 space-y-8">
                <div className="bg-[#4b717a] text-white p-8">
                  <h3 className="text-xl font-sans font-medium mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-6 h-6" />
                    Vigtigt at vide
                  </h3>
                  <div className="space-y-4 text-white/90 leading-relaxed">
                    <p>
                      En årskontrol er fundamentet for din sikkerhed og trivsel med fast medicin.
                    </p>
                    <p>
                      Vi anbefaler altid, at du bestiller tid i god tid, så vi kan nå at få svar på eventuelle prøver inden selve konsultationen.
                    </p>
                    <div className="pt-4 border-t border-white/20">
                      <p className="font-medium text-white">Husk altid:</p>
                      <ul className="mt-2 space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                          At medbringe din medicinliste
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-white flex-shrink-0 mt-0.5" />
                          At få taget prøver 1 uge før samtalen
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="border border-slate-200 p-8">
                  <h3 className="text-xl font-sans font-medium text-black mb-4">
                    Bestil tid
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    Du kan bestille tid til årskontrol via selvbetjeningen eller ved at kontakte klinikken telefonisk.
                  </p>
                  <a 
                    href="https://www.besoeglaegen.dk/Login.aspx?ClinicId=009555"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center w-full px-6 py-3 bg-black text-white font-medium hover:bg-slate-800 transition-colors"
                  >
                    Bestil tid online
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChronicConditions;
